import React, { useEffect } from 'react';
import { Box, Tooltip, Typography } from '@mui/material';
import dayjs from 'dayjs';
import { StatusHistory } from '../services/applicationsApi';

interface StatusTimelineProps {
    statusHistory: StatusHistory[] | null;
    scrollContainerRef?: React.RefObject<HTMLDivElement | null>;
}

const StatusTimeline: React.FC<StatusTimelineProps> = ({ statusHistory, scrollContainerRef }) => {
    useEffect(() => {
        if (scrollContainerRef?.current) {
            scrollContainerRef.current.scrollLeft = scrollContainerRef.current.scrollWidth;
        }
    }, [statusHistory, scrollContainerRef]);

    if (!statusHistory || statusHistory.length === 0) {
        return (
            <Typography sx={{ fontStyle: 'italic', color: 'grey.500', fontSize: '16px', mt: 1 }}>
                No status history available.
            </Typography>
        );
    }

    const sorted = [...statusHistory].sort(
        (a, b) => new Date(a.statusChangedDate).getTime() - new Date(b.statusChangedDate).getTime()
    );

    return (
        <Box sx={{ py: 1 }}>
            <Box
                sx={{
                    display: 'flex',
                    alignItems: 'center',
                    minWidth: 'max-content',
                    px: 1,
                }}
            >
                {sorted.map((item, index) => (
                    <React.Fragment key={index}>
                        <Tooltip
                            arrow
                            placement="top"
                            title={
                                <Box sx={{ p: 0.5 }}>
                                    <Typography variant="body2" sx={{ fontWeight: 700, fontSize: '16px' }}>
                                        {item.oldMigrationStatus} ⟶ {item.newMigrationStatus}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ fontSize: '14px' }}>
                                        <strong>Date:</strong> {dayjs(item.statusChangedDate).format('YYYY-MM-DD HH:mm')}
                                    </Typography>
                                    <Typography variant="caption" display="block" sx={{ fontSize: '14px' }}>
                                        <strong>Changed by:</strong> {item.statusChangedEmail}
                                    </Typography>
                                </Box>
                            }
                            slotProps={{
                                tooltip: {
                                    sx: {
                                        maxWidth: 340,
                                        // fontSize: '30px',
                                        border: '0px',
                                    },
                                }
                            }}
                        >
                            <Box
                                sx={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    alignItems: 'center',
                                    cursor: 'pointer',
                                    minWidth: 120,
                                    '&:hover .timeline-dot': {
                                        transform: 'scale(1.3)',
                                        boxShadow: '0 0 8px rgba(253, 81, 7, 0.6)',
                                    },
                                }}
                            >
                                <Typography
                                    sx={{
                                        fontSize: '14px',
                                        fontWeight: 600,
                                        textAlign: 'center',
                                        maxWidth: 130,
                                        lineHeight: 1.2,
                                        mb: 0.6,
                                        color: '#333',
                                    }}
                                >
                                    {item.newMigrationStatus}
                                </Typography>
                                <Box
                                    className="timeline-dot"
                                    sx={{
                                        width: 14,
                                        height: 14,
                                        borderRadius: '50%',
                                        backgroundColor: '#FD5107',
                                        border: '2px solid #fff',
                                        boxShadow: '0 0 0 2px #FD5107',
                                        zIndex: 1,
                                        transition: 'transform 0.2s, box-shadow 0.2s',
                                    }}
                                />
                                <Typography
                                    sx={{
                                        fontSize: '12px',
                                        color: '#888',
                                        mt: 0.5,
                                        textAlign: 'center',
                                    }}
                                >
                                    {dayjs(item.statusChangedDate).format('YYYY-MM-DD')}
                                </Typography>
                            </Box>
                        </Tooltip>

                        {index < sorted.length - 1 && (
                            <Box
                                sx={{
                                    height: '2px',
                                    flex: '1 1 40px',
                                    minWidth: 40,
                                    backgroundColor: '#FD5107',
                                    position: 'relative',
                                    top: 0,
                                }}
                            />
                        )}
                    </React.Fragment>
                ))}
            </Box>
        </Box>
    );
};

export default StatusTimeline;

